import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { StatusBanner } from '@xing-com/banner';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  AI_SEARCH_ERROR_TYPE,
  AI_SEARCH_ERROR_BANNER,
} from '@xing-com/crate-jobs-constants';
import type { AISearchErrorBannerXinglet } from '@xing-com/crate-jobs-xinglets';
import { zIndexSticky } from '@xing-com/layout-tokens';

const Banner = styled(StatusBanner)`
  z-index: calc(${zIndexSticky} + 1);
`;

type ErrorTypeKey = keyof typeof AI_SEARCH_ERROR_TYPE;
type ErrorType = (typeof AI_SEARCH_ERROR_TYPE)[ErrorTypeKey];

const Container: React.FC = () => {
  const { getSearchParameter, deleteSearchParameter } = useSearchParameter();
  const maybeErrorType = getSearchParameter(AI_SEARCH_ERROR_BANNER);

  const [errorType, setErrorType] = useState<ErrorType | null>(null);

  const handleOnClose = (): void => {
    deleteSearchParameter(AI_SEARCH_ERROR_BANNER);
  };

  const getCopyId = (errorType: ErrorType): string => {
    const { EMPTY } = AI_SEARCH_ERROR_TYPE;
    const messageIds = {
      [EMPTY]: 'JOBS_SEARCH_AI_ERROR_BANNER',
    };
    return messageIds[errorType];
  };

  useEffect(() => {
    if (maybeErrorType) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setErrorType(maybeErrorType as ErrorType);
    }
  }, [maybeErrorType]);

  return (
    <Banner
      show={!!maybeErrorType}
      display="fixed"
      variant="error"
      showCloseButton
      handleOnClose={handleOnClose}
    >
      {errorType ? <FormattedMessage id={getCopyId(errorType)} /> : null}
    </Banner>
  );
};

export default class ErrorBanner implements AISearchErrorBannerXinglet {
  public getComponent() {
    return (): JSX.Element => <Container />;
  }
}
